import React, { useState, useEffect, useContext } from "react";
import {
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Switch,
  Divider,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Collapse,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Settings as SettingsIcon,
  ExpandMore,
  ExpandLess,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";

// Categorias padrão (fallback se empresa não tiver customizadas)
const DEFAULT_CATEGORIES = [
  { value: "", label: "Sem categoria", color: "#9e9e9e" },
  { value: "vendas", label: "Vendas", color: "#4caf50" },
  { value: "suporte", label: "Suporte", color: "#2196f3" },
  { value: "cancelamento", label: "Cancelamento", color: "#f44336" },
  { value: "outros", label: "Outros", color: "#ff9800" },
];

const DEFAULT_CATEGORY_COLORS = {
  vendas: "#4caf50",
  suporte: "#2196f3",
  cancelamento: "#f44336",
  outros: "#ff9800",
};

const FIELD_TYPES = [
  { value: "text", label: "Texto" },
  { value: "number", label: "Número" },
  { value: "select", label: "Lista de opções" },
  { value: "textarea", label: "Texto longo" },
  { value: "checkbox", label: "Checkbox" },
];

const ACTION_TYPES = [
  { value: "notify_user", label: "Notificar usuário" },
  { value: "notify_queue", label: "Notificar fila" },
  { value: "send_message", label: "Enviar mensagem" },
  { value: "webhook", label: "Webhook externo" },
  { value: "add_tag", label: "Adicionar tag" },
  { value: "trigger_flow", label: "Disparar flow" },
];

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    padding: theme.spacing(1, 0),
  },
  settingRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1.5, 0),
  },
  settingDescription: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    marginTop: 2,
  },
  table: {
    minWidth: 650,
  },
  actionCell: {
    width: 120,
  },
  disabledOverlay: {
    opacity: 0.5,
    pointerEvents: "none",
  },
  activeChip: {
    backgroundColor: "#4caf50",
    color: "#fff",
  },
  inactiveChip: {
    backgroundColor: "#9e9e9e",
    color: "#fff",
  },
}));

const FinalizacaoAtendimento = ({ settings, onSettingsChange }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { get: getSetting, update: updateSetting } = useCompanySettings();

  const [motivos, setMotivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editingMotivo, setEditingMotivo] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "", category: "", queueId: "" });

  // Toggles
  const [exigirMotivo, setExigirMotivo] = useState(false);
  const [informarValorVenda, setInformarValorVenda] = useState(false);
  const [observacaoObrigatoria, setObservacaoObrigatoria] = useState(false);
  const [requiredTag, setRequiredTag] = useState(false);


  // Categorias customizáveis
  const [customCategories, setCustomCategories] = useState([]);
  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: "", color: "#9e9e9e" });
  const [showCategories, setShowCategories] = useState(false);

  // Flows (para workflows trigger_flow)
  const [flows, setFlows] = useState([]);

  // Custom Fields
  const [customFields, setCustomFields] = useState([]);
  const [openFieldModal, setOpenFieldModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [fieldForm, setFieldForm] = useState({
    name: "", label: "", fieldType: "text", options: "", required: false, order: 0, queueId: ""
  });
  const [queues, setQueues] = useState([]);
  const [showCustomFields, setShowCustomFields] = useState(false);

  // Workflows
  const [workflows, setWorkflows] = useState([]);
  const [openWorkflowModal, setOpenWorkflowModal] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [workflowForm, setWorkflowForm] = useState({
    name: "", triggerReasonId: "", triggerCategory: "", actionType: "notify_user", actionConfig: {}, active: true
  });
  const [showWorkflows, setShowWorkflows] = useState(false);

  useEffect(() => {
    if (settings) {
      setInformarValorVenda(settings.informarValorVenda || false);
      setExigirMotivo(settings.exigirMotivoFinalizacao || false);
      setObservacaoObrigatoria(settings.observacaoFinalizacaoObrigatoria || false);
    }
    // Carregar tudo em paralelo para reduzir tempo de loading
    Promise.all([
      fetchMotivos(),
      fetchRequiredTag(),
      fetchCustomFields(),
      fetchWorkflows(),
      fetchQueues(),
      fetchCustomCategories(),
      fetchFlows(),
    ]).catch(() => {});
  }, [settings]);

  const fetchRequiredTag = async () => {
    try {
      const setting = await getSetting({ column: "requiredTag" });
      setRequiredTag(setting?.requiredTag === "enabled");
    } catch (err) {
      console.error("Erro ao buscar requiredTag:", err);
    }
  };

  const fetchMotivos = async () => {
    try {
      const { data } = await api.get("/ticketFinalizationReasons");
      setMotivos(data);
    } catch (err) {
      console.error("Erro ao buscar motivos:", err);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const { data } = await api.get("/finalizationCustomFields");
      setCustomFields(data);
    } catch (err) {
      console.error("Erro ao buscar campos customizados:", err);
    }
  };

  const fetchWorkflows = async () => {
    try {
      const { data } = await api.get("/finalizationWorkflows");
      setWorkflows(data);
    } catch (err) {
      console.error("Erro ao buscar workflows:", err);
    }
  };

  const fetchQueues = async () => {
    try {
      const { data } = await api.get("/queue");
      setQueues(data);
    } catch (err) {
      console.error("Erro ao buscar filas:", err);
    }
  };

  const fetchCustomCategories = async () => {
    try {
      const { data } = await api.get("/finalizationCategories");
      setCustomCategories(data);
    } catch (err) {
      console.error("Erro ao buscar categorias:", err);
    }
  };

  const fetchFlows = async () => {
    try {
      const { data } = await api.get("/flowbuilder");
      setFlows(Array.isArray(data) ? data : (data.flows || []));
    } catch (err) {
      console.error("Erro ao buscar flows:", err);
    }
  };

  // Toggle handlers
  const handleToggle = async (column, value, setter, revertValue) => {
    setter(value);
    try {
      await api.put("/companySettings", { column, data: value });
      if (onSettingsChange) {
        onSettingsChange({ ...settings, [column]: value });
      }
      toast.success("Configuração atualizada com sucesso!");
    } catch (err) {
      toast.error("Erro ao atualizar configuração");
      setter(revertValue);
    }
  };

  const handleExigirMotivoChange = (e) =>
    handleToggle("exigirMotivoFinalizacao", e.target.checked, setExigirMotivo, !e.target.checked);
  const handleInformarValorVendaChange = (e) =>
    handleToggle("informarValorVenda", e.target.checked, setInformarValorVenda, !e.target.checked);
  const handleObservacaoObrigatoriaChange = (e) =>
    handleToggle("observacaoFinalizacaoObrigatoria", e.target.checked, setObservacaoObrigatoria, !e.target.checked);

  const handleRequiredTagChange = async (e) => {
    const newValue = e.target.checked;
    setRequiredTag(newValue);
    try {
      await updateSetting({ column: "requiredTag", data: newValue ? "enabled" : "disabled" });
      toast.success("Configuração atualizada com sucesso!");
    } catch (err) {
      toast.error("Erro ao atualizar configuração");
      setRequiredTag(!newValue);
    }
  };

  // Helper: categorias resolvidas (customizadas se existirem, senão padrão)
  const resolvedCategories = customCategories.length > 0
    ? [{ value: "", label: "Sem categoria", color: "#9e9e9e" }, ...customCategories.map(c => ({ value: c.name, label: c.name, color: c.color }))]
    : DEFAULT_CATEGORIES;

  const getCategoryColor = (catValue) => {
    if (!catValue) return "#9e9e9e";
    const custom = customCategories.find(c => c.name === catValue);
    if (custom) return custom.color;
    return DEFAULT_CATEGORY_COLORS[catValue] || "#9e9e9e";
  };

  const getCategoryLabel = (catValue) => {
    if (!catValue) return "—";
    const custom = customCategories.find(c => c.name === catValue);
    if (custom) return custom.name;
    const def = DEFAULT_CATEGORIES.find(c => c.value === catValue);
    return def ? def.label : catValue;
  };

  // ==================== CRUD Categorias ====================
  const handleOpenCategoryModal = (cat = null) => {
    if (cat) {
      setEditingCategory(cat);
      setCategoryForm({ name: cat.name, color: cat.color });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: "", color: "#9e9e9e" });
    }
    setOpenCategoryModal(true);
  };

  const handleCloseCategoryModal = () => {
    setOpenCategoryModal(false);
    setEditingCategory(null);
  };

  const handleCategorySubmit = async () => {
    if (!categoryForm.name.trim()) {
      toast.error("Nome da categoria é obrigatório");
      return;
    }
    setLoading(true);
    try {
      if (editingCategory) {
        await api.put(`/finalizationCategories/${editingCategory.id}`, categoryForm);
        toast.success("Categoria atualizada!");
      } else {
        await api.post("/finalizationCategories", categoryForm);
        toast.success("Categoria criada!");
      }
      handleCloseCategoryModal();
      fetchCustomCategories();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erro ao salvar categoria");
    }
    setLoading(false);
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm("Tem certeza que deseja excluir esta categoria?")) {
      try {
        await api.delete(`/finalizationCategories/${id}`);
        toast.success("Categoria excluída!");
        fetchCustomCategories();
      } catch (err) {
        toast.error(err.response?.data?.error || "Erro ao excluir categoria");
      }
    }
  };

  // ==================== CRUD Motivos ====================
  const handleOpenModal = (motivo = null) => {
    if (motivo) {
      setEditingMotivo(motivo);
      setFormData({ name: motivo.name, description: motivo.description || "", category: motivo.category || "", queueId: motivo.queueId || "" });
    } else {
      setEditingMotivo(null);
      setFormData({ name: "", description: "", category: "", queueId: "" });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingMotivo(null);
    setFormData({ name: "", description: "", category: "", queueId: "" });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Nome do motivo é obrigatório");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...formData, queueId: formData.queueId || null };
      if (editingMotivo) {
        await api.put(`/ticketFinalizationReasons/${editingMotivo.id}`, payload);
        toast.success("Motivo atualizado com sucesso!");
      } else {
        await api.post("/ticketFinalizationReasons", payload);
        toast.success("Motivo criado com sucesso!");
      }
      handleCloseModal();
      fetchMotivos();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erro ao salvar motivo");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Tem certeza que deseja excluir este motivo?")) {
      try {
        await api.delete(`/ticketFinalizationReasons/${id}`);
        toast.success("Motivo excluído com sucesso!");
        fetchMotivos();
      } catch (err) {
        toast.error(err.response?.data?.error || "Erro ao excluir motivo");
      }
    }
  };

  // ==================== CRUD Custom Fields ====================
  const handleOpenFieldModal = (field = null) => {
    if (field) {
      setEditingField(field);
      setFieldForm({
        name: field.name,
        label: field.label,
        fieldType: field.fieldType,
        options: field.options ? field.options.join(", ") : "",
        required: field.required,
        order: field.order,
        queueId: field.queueId || "",
      });
    } else {
      setEditingField(null);
      setFieldForm({ name: "", label: "", fieldType: "text", options: "", required: false, order: 0, queueId: "" });
    }
    setOpenFieldModal(true);
  };

  const handleCloseFieldModal = () => {
    setOpenFieldModal(false);
    setEditingField(null);
  };

  const handleFieldSubmit = async () => {
    if (!fieldForm.name.trim() || !fieldForm.label.trim()) {
      toast.error("Nome e label são obrigatórios");
      return;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(fieldForm.name)) {
      toast.error("Nome do campo deve conter apenas letras, números e _");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...fieldForm,
        options: fieldForm.fieldType === "select" && fieldForm.options
          ? fieldForm.options.split(",").map(o => o.trim()).filter(Boolean)
          : null,
        queueId: fieldForm.queueId || null,
      };
      if (editingField) {
        await api.put(`/finalizationCustomFields/${editingField.id}`, payload);
        toast.success("Campo atualizado com sucesso!");
      } else {
        await api.post("/finalizationCustomFields", payload);
        toast.success("Campo criado com sucesso!");
      }
      handleCloseFieldModal();
      fetchCustomFields();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erro ao salvar campo");
    }
    setLoading(false);
  };

  const handleDeleteField = async (id) => {
    if (window.confirm("Tem certeza que deseja excluir este campo?")) {
      try {
        await api.delete(`/finalizationCustomFields/${id}`);
        toast.success("Campo excluído com sucesso!");
        fetchCustomFields();
      } catch (err) {
        toast.error(err.response?.data?.error || "Erro ao excluir campo");
      }
    }
  };

  // ==================== CRUD Workflows ====================
  const handleOpenWorkflowModal = (wf = null) => {
    if (wf) {
      setEditingWorkflow(wf);
      setWorkflowForm({
        name: wf.name,
        triggerReasonId: wf.triggerReasonId || "",
        triggerCategory: wf.triggerCategory || "",
        actionType: wf.actionType,
        actionConfig: wf.actionConfig || {},
        active: wf.active,
      });
    } else {
      setEditingWorkflow(null);
      setWorkflowForm({
        name: "", triggerReasonId: "", triggerCategory: "", actionType: "notify_user", actionConfig: {}, active: true
      });
    }
    setOpenWorkflowModal(true);
  };

  const handleCloseWorkflowModal = () => {
    setOpenWorkflowModal(false);
    setEditingWorkflow(null);
  };

  const handleWorkflowSubmit = async () => {
    if (!workflowForm.name.trim()) {
      toast.error("Nome do workflow é obrigatório");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...workflowForm,
        triggerReasonId: workflowForm.triggerReasonId || null,
        triggerCategory: workflowForm.triggerCategory || null,
      };
      if (editingWorkflow) {
        await api.put(`/finalizationWorkflows/${editingWorkflow.id}`, payload);
        toast.success("Workflow atualizado com sucesso!");
      } else {
        await api.post("/finalizationWorkflows", payload);
        toast.success("Workflow criado com sucesso!");
      }
      handleCloseWorkflowModal();
      fetchWorkflows();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erro ao salvar workflow");
    }
    setLoading(false);
  };

  const handleDeleteWorkflow = async (id) => {
    if (window.confirm("Tem certeza que deseja excluir este workflow?")) {
      try {
        await api.delete(`/finalizationWorkflows/${id}`);
        toast.success("Workflow excluído com sucesso!");
        fetchWorkflows();
      } catch (err) {
        toast.error(err.response?.data?.error || "Erro ao excluir workflow");
      }
    }
  };

  const handleToggleWorkflow = async (wf) => {
    try {
      await api.put(`/finalizationWorkflows/${wf.id}`, { active: !wf.active });
      fetchWorkflows();
    } catch (err) {
      toast.error("Erro ao alterar status do workflow");
    }
  };

  // Helper for action config
  const updateActionConfig = (key, value) => {
    setWorkflowForm(prev => ({
      ...prev,
      actionConfig: { ...prev.actionConfig, [key]: value }
    }));
  };

  return (
    <div className={classes.root}>
      {/* ============ Regras de Fechamento ============ */}
      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Regras de Fechamento
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          Configure quais informações são obrigatórias ao finalizar um atendimento.
          Estas regras valem para todos os usuários da empresa.
        </Typography>

        <Divider style={{ marginBottom: 8 }} />

        <div className={classes.settingRow}>
          <div>
            <Typography variant="body1">Exigir motivo de finalização</Typography>
            <Typography className={classes.settingDescription}>
              Ao fechar um ticket, o atendente deve selecionar um motivo da lista abaixo.
            </Typography>
          </div>
          <Switch checked={exigirMotivo} onChange={handleExigirMotivoChange} color="primary" />
        </div>

        <Divider />

        <div className={classes.settingRow}>
          <div>
            <Typography variant="body1">Informar valor da venda</Typography>
            <Typography className={classes.settingDescription}>
              Habilita campo de valor monetário no modal de finalização (para equipes comerciais).
            </Typography>
          </div>
          <Switch checked={informarValorVenda} onChange={handleInformarValorVendaChange} color="primary" />
        </div>

        <Divider />

        <div className={classes.settingRow}>
          <div>
            <Typography variant="body1">Observação obrigatória</Typography>
            <Typography className={classes.settingDescription}>
              Exige que o atendente preencha o campo de observação ao finalizar o ticket.
            </Typography>
          </div>
          <Switch checked={observacaoObrigatoria} onChange={handleObservacaoObrigatoriaChange} color="primary" />
        </div>

        <Divider />

        <div className={classes.settingRow}>
          <div>
            <Typography variant="body1">Exigir tag no contato</Typography>
            <Typography className={classes.settingDescription}>
              O atendente só pode fechar o ticket se o contato tiver pelo menos uma tag associada.
            </Typography>
          </div>
          <Switch checked={requiredTag} onChange={handleRequiredTagChange} color="primary" />
        </div>
      </Paper>

      {/* ============ Override por Fila ============ */}
      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Configuração por Fila
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          Cada fila pode sobrescrever as regras globais acima. Se não configurado, herda da empresa.
        </Typography>
        <Divider style={{ marginBottom: 8 }} />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fila</TableCell>
                <TableCell align="center">Exigir Motivo</TableCell>
                <TableCell align="center">Informar Valor</TableCell>
                <TableCell align="center">Observação Obrig.</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queues.map((q) => (
                <TableRow key={q.id}>
                  <TableCell>
                    <Chip
                      size="small"
                      label={q.name}
                      style={{ backgroundColor: q.color, color: "#fff" }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                      <Select
                        value={q.finalizacaoExigirMotivo || ""}
                        onChange={async (e) => {
                          const val = e.target.value || null;
                          try {
                            await api.put(`/queue/${q.id}`, { ...q, finalizacaoExigirMotivo: val });
                            fetchQueues();
                            toast.success(`Configuração da fila "${q.name}" atualizada`);
                          } catch (err) {
                            toast.error("Erro ao atualizar configuração da fila");
                          }
                        }}
                        displayEmpty
                      >
                        <MenuItem value=""><em>Herdar empresa</em></MenuItem>
                        <MenuItem value="true">Sim (forçar)</MenuItem>
                        <MenuItem value="false">Não (desativar)</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="center">
                    <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                      <Select
                        value={q.finalizacaoInformarValorVenda || ""}
                        onChange={async (e) => {
                          const val = e.target.value || null;
                          try {
                            await api.put(`/queue/${q.id}`, { ...q, finalizacaoInformarValorVenda: val });
                            fetchQueues();
                            toast.success(`Configuração da fila "${q.name}" atualizada`);
                          } catch (err) {
                            toast.error("Erro ao atualizar configuração da fila");
                          }
                        }}
                        displayEmpty
                      >
                        <MenuItem value=""><em>Herdar empresa</em></MenuItem>
                        <MenuItem value="true">Sim (forçar)</MenuItem>
                        <MenuItem value="false">Não (desativar)</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="center">
                    <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                      <Select
                        value={q.finalizacaoObservacaoObrigatoria || ""}
                        onChange={async (e) => {
                          const val = e.target.value || null;
                          try {
                            await api.put(`/queue/${q.id}`, { ...q, finalizacaoObservacaoObrigatoria: val });
                            fetchQueues();
                            toast.success(`Configuração da fila "${q.name}" atualizada`);
                          } catch (err) {
                            toast.error("Erro ao atualizar configuração da fila");
                          }
                        }}
                        displayEmpty
                      >
                        <MenuItem value=""><em>Herdar empresa</em></MenuItem>
                        <MenuItem value="true">Sim (forçar)</MenuItem>
                        <MenuItem value="false">Não (desativar)</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {queues.length === 0 && (
          <Box textAlign="center" py={2}>
            <Typography variant="body2" color="textSecondary">
              Nenhuma fila cadastrada.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* ============ Motivos de Finalização ============ */}
      <Paper className={classes.paper + (exigirMotivo ? "" : ` ${classes.disabledOverlay}`)}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <div>
            <Typography variant="h6" className={classes.sectionTitle}>
              Motivos de Finalização
            </Typography>
            {!exigirMotivo && (
              <Typography variant="body2" color="textSecondary">
                Ative "Exigir motivo de finalização" acima para que estes motivos sejam apresentados ao atendente.
              </Typography>
            )}
          </div>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
            disabled={!exigirMotivo}
          >
            Adicionar Motivo
          </Button>
        </Box>

        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Fila</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell className={classes.actionCell}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {motivos.map((motivo) => (
                <TableRow key={motivo.id}>
                  <TableCell>
                    <Typography variant="body1">{motivo.name}</Typography>
                  </TableCell>
                  <TableCell>
                    {motivo.category ? (
                      <Chip
                        size="small"
                        label={getCategoryLabel(motivo.category)}
                        style={{
                          backgroundColor: getCategoryColor(motivo.category),
                          color: "#fff",
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {motivo.queue ? (
                      <Chip
                        size="small"
                        label={motivo.queue.name}
                        style={{ backgroundColor: motivo.queue.color, color: "#fff" }}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">Global</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {motivo.description || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.actionCell}>
                    <IconButton size="small" onClick={() => handleOpenModal(motivo)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(motivo.id)} color="secondary">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {motivos.length === 0 && (
          <Box textAlign="center" py={3}>
            <Typography variant="body2" color="textSecondary">
              Nenhum motivo cadastrado. Clique em "Adicionar Motivo" para começar.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* ============ Categorias Personalizáveis ============ */}
      <Paper className={classes.paper}>
        <div
          className={classes.sectionHeader}
          onClick={() => setShowCategories(!showCategories)}
        >
          <Typography variant="h6" className={classes.sectionTitle}>
            <SettingsIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
            Categorias Personalizáveis
          </Typography>
          {showCategories ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
          Crie categorias customizadas para classificar motivos de finalização. Se não criar nenhuma, serão usadas as categorias padrão.
        </Typography>

        <Collapse in={showCategories}>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenCategoryModal()}
            >
              Adicionar Categoria
            </Button>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Cor</TableCell>
                  <TableCell className={classes.actionCell}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customCategories.map((cat) => (
                  <TableRow key={cat.id}>
                    <TableCell>
                      <Chip
                        size="small"
                        label={cat.name}
                        style={{ backgroundColor: cat.color, color: "#fff" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <span style={{ width: 20, height: 20, borderRadius: 4, backgroundColor: cat.color, display: "inline-block", border: "1px solid #ccc" }} />
                        <Typography variant="body2">{cat.color}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell className={classes.actionCell}>
                      <IconButton size="small" onClick={() => handleOpenCategoryModal(cat)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteCategory(cat.id)} color="secondary">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {customCategories.length === 0 && (
            <Box textAlign="center" py={2}>
              <Typography variant="body2" color="textSecondary">
                Nenhuma categoria customizada. As categorias padrão (Vendas, Suporte, Cancelamento, Outros) estão sendo usadas.
              </Typography>
            </Box>
          )}
        </Collapse>
      </Paper>

      {/* ============ Campos Customizados ============ */}
      <Paper className={classes.paper}>
        <div
          className={classes.sectionHeader}
          onClick={() => setShowCustomFields(!showCustomFields)}
        >
          <Typography variant="h6" className={classes.sectionTitle}>
            <SettingsIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
            Campos Customizados
          </Typography>
          {showCustomFields ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
          Defina campos extras que aparecem no modal de finalização. Podem ser globais ou por fila.
        </Typography>

        <Collapse in={showCustomFields}>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenFieldModal()}
            >
              Adicionar Campo
            </Button>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Label</TableCell>
                  <TableCell>Nome (chave)</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Fila</TableCell>
                  <TableCell>Obrigatório</TableCell>
                  <TableCell className={classes.actionCell}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customFields.map((field) => (
                  <TableRow key={field.id}>
                    <TableCell>{field.label}</TableCell>
                    <TableCell>
                      <code style={{ fontSize: "0.8rem", background: "#f5f5f5", padding: "2px 6px", borderRadius: 3 }}>
                        {field.name}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={FIELD_TYPES.find(t => t.value === field.fieldType)?.label || field.fieldType} />
                    </TableCell>
                    <TableCell>
                      {field.queue ? (
                        <Chip
                          size="small"
                          label={field.queue.name}
                          style={{ backgroundColor: field.queue.color, color: "#fff" }}
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">Todas</Typography>
                      )}
                    </TableCell>
                    <TableCell>{field.required ? "Sim" : "Não"}</TableCell>
                    <TableCell className={classes.actionCell}>
                      <IconButton size="small" onClick={() => handleOpenFieldModal(field)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteField(field.id)} color="secondary">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {customFields.length === 0 && (
            <Box textAlign="center" py={2}>
              <Typography variant="body2" color="textSecondary">
                Nenhum campo customizado. Os campos padrão (motivo, valor, observação) já são exibidos conforme as regras acima.
              </Typography>
            </Box>
          )}
        </Collapse>
      </Paper>

      {/* ============ Workflows Pós-Fechamento ============ */}
      <Paper className={classes.paper}>
        <div
          className={classes.sectionHeader}
          onClick={() => setShowWorkflows(!showWorkflows)}
        >
          <Typography variant="h6" className={classes.sectionTitle}>
            <PlayIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
            Workflows Pós-Fechamento
          </Typography>
          {showWorkflows ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
          Ações automáticas disparadas quando um ticket é finalizado com determinado motivo ou categoria.
        </Typography>

        <Collapse in={showWorkflows}>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenWorkflowModal()}
            >
              Adicionar Workflow
            </Button>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Gatilho</TableCell>
                  <TableCell>Ação</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell className={classes.actionCell}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workflows.map((wf) => (
                  <TableRow key={wf.id}>
                    <TableCell>{wf.name}</TableCell>
                    <TableCell>
                      {wf.triggerReason ? (
                        <Chip size="small" label={wf.triggerReason.name} variant="outlined" />
                      ) : wf.triggerCategory ? (
                        <Chip
                          size="small"
                          label={getCategoryLabel(wf.triggerCategory)}
                          style={{ backgroundColor: getCategoryColor(wf.triggerCategory), color: "#fff" }}
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">Qualquer motivo</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={ACTION_TYPES.find(a => a.value === wf.actionType)?.label || wf.actionType} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={wf.active ? "Clique para desativar" : "Clique para ativar"}>
                        <Chip
                          size="small"
                          label={wf.active ? "Ativo" : "Inativo"}
                          className={wf.active ? classes.activeChip : classes.inactiveChip}
                          onClick={() => handleToggleWorkflow(wf)}
                          style={{ cursor: "pointer" }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell className={classes.actionCell}>
                      <IconButton size="small" onClick={() => handleOpenWorkflowModal(wf)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteWorkflow(wf.id)} color="secondary">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {workflows.length === 0 && (
            <Box textAlign="center" py={2}>
              <Typography variant="body2" color="textSecondary">
                Nenhum workflow configurado. Crie workflows para automatizar ações após o fechamento de tickets.
              </Typography>
            </Box>
          )}
        </Collapse>
      </Paper>

      {/* ============ Modal CRUD Motivos ============ */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingMotivo ? "Editar Motivo" : "Adicionar Motivo"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Nome do Motivo"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                variant="outlined"
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined" margin="dense">
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Categoria"
                >
                  {resolvedCategories.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        {cat.color && cat.value && (
                          <span style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: cat.color, display: "inline-block" }} />
                        )}
                        {cat.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" margin="dense">
                <InputLabel>Fila (opcional)</InputLabel>
                <Select
                  value={formData.queueId}
                  onChange={(e) => setFormData({ ...formData, queueId: e.target.value })}
                  label="Fila (opcional)"
                >
                  <MenuItem value="">
                    <em>Global (todas as filas)</em>
                  </MenuItem>
                  {queues.map((q) => (
                    <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="textSecondary">
                Se selecionar uma fila, este motivo só aparecerá na finalização de tickets dessa fila.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição (opcional)"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                variant="outlined"
                margin="dense"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained" disabled={loading}>
            {loading ? "Salvando..." : editingMotivo ? "Atualizar" : "Adicionar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ Modal CRUD Custom Fields ============ */}
      <Dialog open={openFieldModal} onClose={handleCloseFieldModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingField ? "Editar Campo Customizado" : "Adicionar Campo Customizado"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome (chave)"
                value={fieldForm.name}
                onChange={(e) => setFieldForm({ ...fieldForm, name: e.target.value.replace(/[^a-zA-Z0-9_]/g, "") })}
                variant="outlined"
                margin="dense"
                required
                helperText="Apenas letras, números e _ (ex: numero_contrato)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Label (exibição)"
                value={fieldForm.label}
                onChange={(e) => setFieldForm({ ...fieldForm, label: e.target.value })}
                variant="outlined"
                margin="dense"
                required
                helperText="Ex: Número do contrato"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" margin="dense">
                <InputLabel>Tipo do campo</InputLabel>
                <Select
                  value={fieldForm.fieldType}
                  onChange={(e) => setFieldForm({ ...fieldForm, fieldType: e.target.value })}
                  label="Tipo do campo"
                >
                  {FIELD_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" margin="dense">
                <InputLabel>Fila (opcional)</InputLabel>
                <Select
                  value={fieldForm.queueId}
                  onChange={(e) => setFieldForm({ ...fieldForm, queueId: e.target.value })}
                  label="Fila (opcional)"
                >
                  <MenuItem value="">Todas as filas</MenuItem>
                  {queues.map((q) => (
                    <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {fieldForm.fieldType === "select" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Opções (separadas por vírgula)"
                  value={fieldForm.options}
                  onChange={(e) => setFieldForm({ ...fieldForm, options: e.target.value })}
                  variant="outlined"
                  margin="dense"
                  helperText="Ex: Opção 1, Opção 2, Opção 3"
                />
              </Grid>
            )}
            <Grid item xs={6} sm={3}>
              <Box display="flex" alignItems="center" height="100%">
                <Typography variant="body2" style={{ marginRight: 8 }}>Obrigatório</Typography>
                <Switch
                  checked={fieldForm.required}
                  onChange={(e) => setFieldForm({ ...fieldForm, required: e.target.checked })}
                  color="primary"
                />
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Ordem"
                type="number"
                value={fieldForm.order}
                onChange={(e) => setFieldForm({ ...fieldForm, order: parseInt(e.target.value) || 0 })}
                variant="outlined"
                margin="dense"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFieldModal} disabled={loading}>Cancelar</Button>
          <Button onClick={handleFieldSubmit} color="primary" variant="contained" disabled={loading}>
            {loading ? "Salvando..." : editingField ? "Atualizar" : "Adicionar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ Modal CRUD Categorias ============ */}
      <Dialog open={openCategoryModal} onClose={handleCloseCategoryModal} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editingCategory ? "Editar Categoria" : "Adicionar Categoria"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome da Categoria"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                variant="outlined"
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                <Typography variant="body2">Cor:</Typography>
                <input
                  type="color"
                  value={categoryForm.color}
                  onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                  style={{ width: 40, height: 30, border: "none", cursor: "pointer" }}
                />
                <Typography variant="body2" color="textSecondary">{categoryForm.color}</Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCategoryModal} disabled={loading}>Cancelar</Button>
          <Button onClick={handleCategorySubmit} color="primary" variant="contained" disabled={loading}>
            {loading ? "Salvando..." : editingCategory ? "Atualizar" : "Adicionar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ Modal CRUD Workflows ============ */}
      <Dialog open={openWorkflowModal} onClose={handleCloseWorkflowModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingWorkflow ? "Editar Workflow" : "Adicionar Workflow"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome do Workflow"
                value={workflowForm.name}
                onChange={(e) => setWorkflowForm({ ...workflowForm, name: e.target.value })}
                variant="outlined"
                margin="dense"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ marginBottom: 4 }}>Gatilho</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" margin="dense">
                <InputLabel>Motivo específico</InputLabel>
                <Select
                  value={workflowForm.triggerReasonId}
                  onChange={(e) => setWorkflowForm({ ...workflowForm, triggerReasonId: e.target.value })}
                  label="Motivo específico"
                >
                  <MenuItem value="">Qualquer motivo</MenuItem>
                  {motivos.map((m) => (
                    <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" margin="dense">
                <InputLabel>Ou por categoria</InputLabel>
                <Select
                  value={workflowForm.triggerCategory}
                  onChange={(e) => setWorkflowForm({ ...workflowForm, triggerCategory: e.target.value })}
                  label="Ou por categoria"
                  disabled={!!workflowForm.triggerReasonId}
                >
                  <MenuItem value="">Qualquer categoria</MenuItem>
                  {resolvedCategories.filter(c => c.value).map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ marginBottom: 4 }}>Ação</Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" margin="dense">
                <InputLabel>Tipo de ação</InputLabel>
                <Select
                  value={workflowForm.actionType}
                  onChange={(e) => setWorkflowForm({ ...workflowForm, actionType: e.target.value, actionConfig: {} })}
                  label="Tipo de ação"
                >
                  {ACTION_TYPES.map((a) => (
                    <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Config dinâmica por tipo de ação */}
            {workflowForm.actionType === "notify_user" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Mensagem de notificação"
                  value={workflowForm.actionConfig.message || ""}
                  onChange={(e) => updateActionConfig("message", e.target.value)}
                  variant="outlined"
                  margin="dense"
                  multiline
                  rows={2}
                  placeholder="Ticket #{ticketId} finalizado com motivo: {motivo}"
                />
              </Grid>
            )}

            {workflowForm.actionType === "webhook" && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="URL do Webhook"
                    value={workflowForm.actionConfig.url || ""}
                    onChange={(e) => updateActionConfig("url", e.target.value)}
                    variant="outlined"
                    margin="dense"
                    placeholder="https://api.example.com/webhook"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Será enviado um POST com: ticketId, companyId, motivo, category, timestamp
                  </Typography>
                </Grid>
              </>
            )}

            {workflowForm.actionType === "send_message" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Mensagem para enviar"
                  value={workflowForm.actionConfig.message || ""}
                  onChange={(e) => updateActionConfig("message", e.target.value)}
                  variant="outlined"
                  margin="dense"
                  multiline
                  rows={3}
                  placeholder="Obrigado pelo seu contato! Seu atendimento foi finalizado."
                />
              </Grid>
            )}

            {workflowForm.actionType === "trigger_flow" && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined" margin="dense">
                    <InputLabel>Flow a disparar</InputLabel>
                    <Select
                      value={workflowForm.actionConfig.flowId || ""}
                      onChange={(e) => updateActionConfig("flowId", e.target.value)}
                      label="Flow a disparar"
                    >
                      <MenuItem value=""><em>Selecione um flow</em></MenuItem>
                      {flows.map((f) => (
                        <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    O flow será disparado automaticamente para o contato do ticket quando finalizado com o gatilho configurado.
                  </Typography>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Box display="flex" alignItems="center">
                <Typography variant="body2" style={{ marginRight: 8 }}>Ativo</Typography>
                <Switch
                  checked={workflowForm.active}
                  onChange={(e) => setWorkflowForm({ ...workflowForm, active: e.target.checked })}
                  color="primary"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWorkflowModal} disabled={loading}>Cancelar</Button>
          <Button onClick={handleWorkflowSubmit} color="primary" variant="contained" disabled={loading}>
            {loading ? "Salvando..." : editingWorkflow ? "Atualizar" : "Adicionar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FinalizacaoAtendimento;
